export interface JobsiteBusinessService {
  name?: string;
}

export type JobsiteBusinessServiceValue = JobsiteBusinessService | string;

export interface JobsiteServiceAreaBoundary {
  type: "Polygon" | "MultiPolygon";
  coordinates: number[][][] | number[][][][];
}

export interface JobsiteBusinessServiceArea {
  id?: string;
  name?: string;
  /** The city segment of its town page URLs; not always the slugified name, since two cities can share one. */
  slug?: string;
  displayName?: string;
  state?: string;
  latitude?: number | null;
  longitude?: number | null;
  boundary?: JobsiteServiceAreaBoundary | null;
  isPrimary?: boolean;
  surroundingAreas?: boolean;
}

export interface JobsiteBusinessContext {
  businessId?: string;
  /** Canonical booking slug — BookingWidget resolves /api/booking/public/:slug against this. */
  slug?: string;
  city?: string;
  email?: string;
  hasTrackingPhone?: boolean;
  is24Hours?: boolean;
  /**
   * Whether the business takes emergency work. The one writer is the toggle in
   * Business Settings, which is why no component offers a field for it.
   *
   * Null means "not asserted", so only an explicit true renders a claim.
   */
  emergencyService?: boolean | null;
  /** Separate emergency line, when the owner has set one. Falls back to `phone`. */
  emergencyPhone?: string | null;
  name?: string;
  phone?: string;
  services?: JobsiteBusinessServiceValue[];
  serviceAreas?: JobsiteBusinessServiceArea[];
  state?: string;
  formattedAddress?: string | null;
  /** Google's listing id. Null until GBP is connected — rows that need it
   *  (review link, directions place pin) degrade rather than guess. */
  placeId?: string | null;
  /** Absolute booking-page URL, baked by resolveSiteBusinessMetadata so the
   *  editor preview and the static build always agree on the host. */
  bookingUrl?: string;
  bookingEnabled?: boolean;
  googleRating?: number | null;
  googleReviewCount?: number | null;
  logoUrl?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  linkedinUrl?: string | null;
  twitterUrl?: string | null;
  telephone?: string;
  url?: string;
  website?: string;
  // CTA values sourced from the Site row (1:1 with Business). Null means
  // "use the frontend default" — see packages/jobsite-components/src/lib/cta-defaults.ts.
  ctaPrimaryLabel?: string | null;
  ctaOfferLine?: string | null;
  ctaPresetSeed?: string | null;
}

export interface JobsiteRuntimeConfig {
  apiUrl?: string;
  /** Where a lead goes when `apiUrl` is down (SP-400). Absent disables the fallback. */
  backupApiUrl?: string;
  business?: JobsiteBusinessContext;
  businessId?: string;
  siteId?: string;
  siteUrl?: string;
  turnstileSiteKey?: string;
  variant?: string;
  /** Google Maps JS API key, for the contact form's address autocomplete. Baked
   *  into site-data at build time; absent in the editor preview (→ plain text). */
  googleMapsApiKey?: string;
  /** Set by the dashboard's jobsite editor (see smart-link's edit-mode guard). */
  isEditing?: boolean;
  /**
   * How the header is positioned. A transparent header is `fixed` with no
   * spacer, so the first in-flow section sits underneath it unless it offsets.
   */
  navigationStyle?: "solid" | "transparent" | "sticky";
  /** Full navigation props where a surface provides them (root.render reads these). */
  navigation?: { style?: "solid" | "transparent" | "sticky" } & Record<string, unknown>;
}

interface WindowWithJobsiteConfig extends Window {
  __SERVICEPRO_CONFIG__?: JobsiteRuntimeConfig;
}

export function getWindowJobsiteConfig(): JobsiteRuntimeConfig {
  if (typeof window === "undefined") return {};
  // Check parent window first (Puck editor renders preview in an iframe)
  try {
    if (
      window.parent &&
      window.parent !== window &&
      (window.parent as unknown as WindowWithJobsiteConfig).__SERVICEPRO_CONFIG__
    ) {
      return (window.parent as unknown as WindowWithJobsiteConfig).__SERVICEPRO_CONFIG__!;
    }
  } catch {
    // Cross-origin access denied — fall through
  }
  return (window as WindowWithJobsiteConfig).__SERVICEPRO_CONFIG__ ?? {};
}
